/**
 * Dependencies.
 */
import '@/plugins/icons.plugin';
import '@/plugins/services.plugin';
import '@/plugins/ui.plugin';
import 'leaflet/dist/leaflet.css';
import 'reflect-metadata';

import { LanguageModel } from '@prz-dp/services';
import { PkRoot } from '@prz-dp/ui-next';
import { AxiosRequestConfig } from 'axios';
import Vue from 'vue';

import Main from '@/modules/main';
import applicationConstant from '@/modules/main/constants/application.constant';
import { i18n } from '@/plugins/i18n.plugin';
import router from '@/plugins/router.plugin';
import store from '@/plugins/store.plugin';
import vuetify from '@/plugins/vuetify.plugin';

/**
 * Production tip.
 */
Vue.config.productionTip = false;

/**
 * Use `Main`.
 */
Vue.use(Main);

/**
 * Vue instance.
 */
const vm = new Vue({
  components: {
    PkRoot
  },
  created() {
    this.$kit.loading = true;

    // HTTP.
    this.$kit.http.setupBaseUrl(applicationConstant.baseUrl);

    this.$kit.http.setupPrivateEndpoint((url: string) => {
      const { auth } = applicationConstant || {};
      const { privateBaseUrl } = auth || {};

      let isPrivate = false;

      if (privateBaseUrl) {
        isPrivate = url.match(privateBaseUrl) !== null;
      }

      return isPrivate;
    });

    this.$kit.http.addRequestInterceptor((config: AxiosRequestConfig) =>
      Vue.prototype.$kit.lang
        .getCurrentLanguage()
        .then(({ locale }: LanguageModel) => {
          config.headers = {
            ...config.headers,
            'Accept-Language': `${locale},en-US;q=0.8,pt;q=0.6`
          };

          return config;
        })
    );

    // Language.
    this.$kit.lang.setSupportedLanguages([
      new LanguageModel({ code: 'de', locale: 'de-DE', name: 'Deutsch' }),
      new LanguageModel({ code: 'en', locale: 'en-US', name: 'English' }),
      new LanguageModel({ code: 'es', locale: 'es-ES', name: 'Español' }),
      new LanguageModel({ code: 'it', locale: 'it-IT', name: 'Italiano' }),
      new LanguageModel({ code: 'pt', locale: 'pt-PT', name: 'Português' })
    ]);

    this.$kit.lang.setFallback('en');

    const language = this.$kit.lang.supportedLanguages.filter(
      lang =>
        lang.locale.toLowerCase().split('-')[0] ===
        navigator.language.toLowerCase().split('-')[0]
    )[0];

    this.$kit.lang
      .setCurrentLanguage(language)
      .then((lang: LanguageModel) => {
        if (!lang) {
          console.error('UPPS! language error');
        } else {
          this.$root.$i18n.locale = lang.locale;
          this.$moment.locale(lang.code);
        }
      })
      .catch(event => {
        this.$kit.notify.error('Error setting current language', event);
      })
      .finally(() => {
        this.$kit.loading = false;
      });
  },
  i18n,
  render: h => h(PkRoot),
  router,
  store,
  vuetify
}).$mount('#app');

if (process.env.BUILD_MODE === 'local' || process.env.BUILD_MODE === 'dev') {
  (window as any).vm = vm;
}
